import ProgressBar from "@/components/ProgressBar/ProgressBar";
import convertBytesToSize from "@/utils/convertBytesToSize";
import {
  Loader,
  styled,
  Tooltip,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { CheckIcon, XIcon } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { FileUploaderFile } from "../../Contexts";
import { FileStatus } from "../../files.types";
import { UploadProgress } from "../../Uploader";

const StyledContainer = styled.div`
  & .content {
    overflow: hidden;
  }
  &.upload-item {
    width: 100%;
    height: 40px;
    padding: 5px;
    border-radius: 5px;
    overflow-y: hidden;
    color: ${({ theme }) => theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    .content {
      display: flex;
      align-items: center;
      align-content: center;
      padding: 5px;
      border-radius: 3px;
      overflow: hidden;
    }
    .file-name {
      width: 80%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
    }
  }
  &.upload-item:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;

export const UploadItem = ({ file }: { file: FileUploaderFile }) => {
  const theme = useTheme();
  const [status, setStatus] = useState(file.status);
  const [hashProgress, setHashProgress] = useState<UploadProgress>();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>();
  const { enqueueSnackbar } = useSnackbar();

  const onFileUploaded = ({ detail }: { detail: UploadProgress }) => {
    const { uuid } = detail;
    if (file.uuid === uuid) {
      if (detail.type === "error") {
        enqueueSnackbar("File upload failed", {
          variant: "error",
        });
        setStatus(FileStatus.FAILED);
      } else if (detail.type === "complete") {
        setStatus(FileStatus.ACTIVE);
      }
    }
  };

  const onProgress = ({ detail }: { detail: UploadProgress }) => {
    if (file.uuid === detail.uuid) {
      if (detail.type === "upload") {
        setUploadProgress(detail);
      } else if (detail.type === "hash") {
        setHashProgress(detail);
      } else if (detail.type === "complete") {
        onFileUploaded({ detail });
      } else if (detail.type === "error") {
        setStatus(FileStatus.FAILED);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("file-uploader:file-progress", onProgress as any);

    return () => {
      document.removeEventListener(
        "file-uploader:file-progress",
        onProgress as any
      );
    };
  }, [file]);

  return (
    <Tooltip
      content={`${file.name}\nSize: ${convertBytesToSize(
        file.size || 0
      )}\nUpload Progress ${
        uploadProgress?.percentage || 0
      }% | ${convertBytesToSize(uploadProgress?.loaded || 0)}\nHash Progress ${
        hashProgress?.percentage || 0
      }% | ${convertBytesToSize(hashProgress?.loaded || 0)}`}
      align="start"
    >
      <StyledContainer className={"upload-item"}>
        <div className="content">
          <div className="file-name">{file.name}</div>
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              overflow: "hidden",
            }}
          >
            {status === FileStatus.UPLOADING && <Loader size={14} />}
            {status === FileStatus.ACTIVE && (
              <CheckIcon size={14} color="limegreen" />
            )}
            {status === FileStatus.FAILED && <XIcon size={14} color="red" />}
          </div>
        </div>
        {status === FileStatus.UPLOADING && (
          <div style={{ width: "80%" }}>
            <ProgressBar
              percent={uploadProgress?.percentage || 0}
              height={3}
              color={theme.palette.primary.main}
            />
          </div>
        )}
        {status === FileStatus.UPLOADING && (
          <div style={{ width: "80%", marginTop: 2.5 }}>
            <ProgressBar
              percent={hashProgress?.percentage || 0}
              height={3}
              color="green"
            />
          </div>
        )}
      </StyledContainer>
    </Tooltip>
  );
};
